import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FractalCanvas } from './FractalCanvas';

export const FRACTAL_DPR_CHANGE_EVENT = 'fractal:dprchange';
export const MAX_RENDER_PIXELS = 1920 * 1500;

export function deviceScale() {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  return Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
}

export function cappedBackingSize(cssWidth, cssHeight, maxPixels = MAX_RENDER_PIXELS) {
  if (!(cssWidth > 0 && cssHeight > 0)) return null;
  const scale = Math.min(deviceScale(), Math.sqrt(maxPixels / (cssWidth * cssHeight)));
  let width = Math.max(1, Math.round(cssWidth * scale));
  let height = Math.max(1, Math.round(cssHeight * scale));
  if (width * height > maxPixels) {
    width = Math.min(maxPixels, Math.max(1, Math.floor(cssWidth * scale)));
    height = Math.min(Math.floor(maxPixels / width), Math.max(1, Math.floor(cssHeight * scale)));
  }
  return { width, height };
}

export function watchDevicePixelRatio(onChange) {
  let mediaQuery;
  const unbindMediaQuery = () => {
    if (mediaQuery?.removeEventListener) mediaQuery.removeEventListener('change', handleDprChange);
    else mediaQuery?.removeListener?.(handleDprChange);
  };
  function handleDprChange() {
    onChange();
    bindMediaQuery();
  }
  function bindMediaQuery() {
    unbindMediaQuery();
    if (typeof window.matchMedia !== 'function') return;
    mediaQuery = window.matchMedia(`(resolution: ${deviceScale()}dppx)`);
    if (mediaQuery?.addEventListener) mediaQuery.addEventListener('change', handleDprChange);
    else mediaQuery?.addListener?.(handleDprChange);
  }
  bindMediaQuery();
  window.addEventListener(FRACTAL_DPR_CHANGE_EVENT, handleDprChange);
  return () => {
    window.removeEventListener(FRACTAL_DPR_CHANGE_EVENT, handleDprChange);
    unbindMediaQuery();
  };
}

export function ResponsiveFractalCanvas({
  width: presetWidth,
  height: presetHeight,
  fullWidth = false,
  deferUntilVisible = false,
  ...props
}) {
  const wrapperRef = useRef(null);
  const initialRangesRef = useRef(null);
  const [dimensions, setDimensions] = useState(null);
  const [visible, setVisible] = useState(
    () => !deferUntilVisible || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (visible) return undefined;
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(item => item.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [visible]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const updateSize = () => {
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const cssWidth = rect.width || wrapper.clientWidth;
      if (cssWidth <= 0) return;
      const cssHeight = fullWidth ? (rect.height || wrapper.clientHeight) : 0;
      let width;
      let height;
      if (fullWidth && cssHeight > 0) {
        ({ width, height } = cappedBackingSize(cssWidth, cssHeight));
      } else {
        width = Math.min(presetWidth, Math.max(1, Math.round(cssWidth * deviceScale())));
        height = Math.min(presetHeight, Math.max(1, Math.round(width * presetHeight / presetWidth)));
      }
      setDimensions(previous => previous?.width === width && previous?.height === height
        ? previous : { width, height });
    };

    updateSize();
    const stopWatchingDpr = watchDevicePixelRatio(updateSize);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateSize);
    observer?.observe(wrapper);
    window.addEventListener('resize', updateSize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateSize);
      stopWatchingDpr();
    };
  }, [presetWidth, presetHeight, fullWidth]);

  if (dimensions && !initialRangesRef.current && fullWidth && props.xRange && props.yRange) {
    const aspect = dimensions.width / dimensions.height;
    const centerX = (props.xRange[0] + props.xRange[1]) / 2;
    const centerY = (props.yRange[0] + props.yRange[1]) / 2;
    const spanY = Math.max(props.yRange[1] - props.yRange[0], (props.xRange[1] - props.xRange[0]) / aspect);
    const spanX = spanY * aspect;
    initialRangesRef.current = {
      xRange: [centerX - spanX / 2, centerX + spanX / 2],
      yRange: [centerY - spanY / 2, centerY + spanY / 2],
    };
  }

  return <div className={`responsive-fractal${fullWidth ? ' responsive-fractal-full-width' : ''}`} ref={wrapperRef}
    style={{ maxWidth: fullWidth ? undefined : presetWidth, '--fractal-aspect': `${presetWidth} / ${presetHeight}` }}>
    {visible && dimensions && <FractalCanvas width={dimensions.width} height={dimensions.height} {...props} {...initialRangesRef.current} />}
  </div>;
}
